// Portfolio Administration v1.0 PR 5 — Performance tab on the Local
// Enforcer registry detail page. Renders the 4-rating shape (service
// quality / responsiveness / outcome / cost predictability — overall is
// computed by the backend), the last-review-date, and a "Record review"
// affordance gated by E1 / ADMIN per spec §14. Persistent review-
// history storage is not in PR 1's schema — the visualisation uses the
// last-known-rating set on LocalEnforcer; tickler scheduling lives in
// the PR 2c worker.

import { useState } from "react";
import {
  AlertTriangle,
  Star as StarIcon,
} from "lucide-react";
import {
  Button,
  Card,
  Input,
  Label,
  Textarea,
} from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useRecordLocalEnforcerPerformanceReview } from "@/hooks/portfolioAdmin/useRegistries";
import { RatingStars } from "@/components/portfolioAdmin/registry/RatingStars";
import type { LocalEnforcerDetailDto } from "@/api/portfolioAdmin/pr4bTypes";
import { formatCalendarDate, isoDateInTz, useUserTimeZone } from "@/utils/datetime";

export function EnforcerPerformanceTab({
  enforcer,
}: {
  enforcer: LocalEnforcerDetailDto;
}) {
  const auth = useAuth();
  const canReview =
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Enforcer.Manage");
  const record = useRecordLocalEnforcerPerformanceReview(enforcer.id);
  const tz = useUserTimeZone();

  const [open, setOpen] = useState(false);
  const [overall, setOverall] = useState<number>(
    enforcer.overallRating ?? 3,
  );
  const [responsiveness, setResponsiveness] = useState<number>(
    enforcer.responsivenessRating ?? 3,
  );
  const [outcome, setOutcome] = useState<number>(
    enforcer.outcomeRating ?? 3,
  );
  const [costPred, setCostPred] = useState<number>(
    enforcer.costPredictabilityRating ?? 3,
  );
  const [reviewDate, setReviewDate] = useState(
    isoDateInTz(new Date(), tz),
  );
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Spec §9 — two consecutive sub-2.0 overall reviews flag the enforcer
  // for E1 suspension review. We can only check the current rating here
  // (full history requires the EnforcerPerformanceReview history entity
  // which is not in PR 1's schema); the banner is therefore conservative
  // and only fires on the current rating until the history table lands.
  const flagged =
    enforcer.overallRating !== null && enforcer.overallRating < 2.0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await record.mutateAsync({
        overallRating: overall,
        responsivenessRating: responsiveness,
        outcomeRating: outcome,
        costPredictabilityRating: costPred,
        reviewDate,
        notes: notes || null,
      });
      setOpen(false);
      setNotes("");
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Could not record review.",
      );
    }
  };

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid="enforcer-performance-tab"
    >
      {flagged ? (
        <Card
          className="flex items-start gap-space-2 border border-status-error/40 bg-status-error/5"
          data-testid="enforcer-termination-banner"
        >
          <AlertTriangle
            size={18}
            className="text-status-error"
            aria-hidden="true"
          />
          <div className="flex flex-col">
            <span className="font-display font-bold text-small">
              Suspension review recommended
            </span>
            <span className="text-small text-gray-mid">
              Overall rating is below 2.0. E1 to evaluate per spec §9.
            </span>
          </div>
        </Card>
      ) : null}

      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Current ratings</h3>
        <dl className="grid grid-cols-[200px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">Overall</dt>
          <dd className="m-0">
            <RatingStars
              rating={enforcer.overallRating}
              testId="enforcer-rating-overall"
              size="md"
            />
          </dd>
          <dt className="text-gray-mid">Responsiveness</dt>
          <dd className="m-0">
            <RatingStars
              rating={enforcer.responsivenessRating}
              testId="enforcer-rating-responsiveness"
            />
          </dd>
          <dt className="text-gray-mid">Outcome</dt>
          <dd className="m-0">
            <RatingStars
              rating={enforcer.outcomeRating}
              testId="enforcer-rating-outcome"
            />
          </dd>
          <dt className="text-gray-mid">Cost predictability</dt>
          <dd className="m-0">
            <RatingStars
              rating={enforcer.costPredictabilityRating}
              testId="enforcer-rating-cost-predictability"
            />
          </dd>
          <dt className="text-gray-mid">Last reviewed</dt>
          <dd className="m-0">
            {enforcer.lastReviewDate ? formatCalendarDate(enforcer.lastReviewDate) : "Never"}
            {enforcer.lastReviewerUserId
              ? ` · by ${enforcer.lastReviewerUserId}`
              : ""}
          </dd>
        </dl>
      </Card>

      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">
          Semi-annual review cadence
        </h3>
        <p className="text-small text-gray-mid italic m-0">
          Performance reviews are recorded on a semi-annual cadence. The
          PR 2c worker emits a calendar tickler 6 months after each
          review.
        </p>
      </Card>

      {canReview ? (
        <Card className="flex flex-col gap-space-2">
          <div className="flex items-center justify-between">
            <h3 className="font-display font-bold text-h3 m-0">
              Record performance review
            </h3>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setOpen((v) => !v)}
              data-testid="enforcer-record-review-toggle"
            >
              <StarIcon size={14} aria-hidden="true" />
              {open ? "Close" : "Record review"}
            </Button>
          </div>
          {open ? (
            <form
              onSubmit={submit}
              className="flex flex-col gap-space-2"
              data-testid="enforcer-record-review-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2">
                <RatingNumberInput
                  label="Overall (1-5)"
                  value={overall}
                  onChange={setOverall}
                  testId="review-overall"
                />
                <RatingNumberInput
                  label="Responsiveness"
                  value={responsiveness}
                  onChange={setResponsiveness}
                  testId="review-responsiveness"
                />
                <RatingNumberInput
                  label="Outcome"
                  value={outcome}
                  onChange={setOutcome}
                  testId="review-outcome"
                />
                <RatingNumberInput
                  label="Cost predictability"
                  value={costPred}
                  onChange={setCostPred}
                  testId="review-cost-pred"
                />
              </div>
              <div className="flex flex-col gap-space-1">
                <Label htmlFor="review-date">Review date</Label>
                <Input
                  id="review-date"
                  type="date"
                  value={reviewDate}
                  onChange={(e) => setReviewDate(e.target.value)}
                  required
                />
              </div>
              <div className="flex flex-col gap-space-1">
                <Label htmlFor="review-notes">Notes</Label>
                <Textarea
                  id="review-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
              {error ? (
                <p
                  className="text-small text-status-error m-0"
                  data-testid="review-error"
                >
                  {error}
                </p>
              ) : null}
              <div className="flex justify-end gap-space-2">
                <Button
                  type="submit"
                  disabled={record.isPending}
                  data-testid="enforcer-record-review-submit"
                >
                  {record.isPending ? "Recording…" : "Submit review"}
                </Button>
              </div>
            </form>
          ) : null}
        </Card>
      ) : null}
    </div>
  );
}

interface RatingInputProps {
  label: string;
  value: number;
  onChange: (next: number) => void;
  testId: string;
}
function RatingNumberInput({ label, value, onChange, testId }: RatingInputProps) {
  return (
    <div className="flex flex-col gap-space-1">
      <Label>{label}</Label>
      <Input
        type="number"
        min={1}
        max={5}
        step={0.1}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
      />
    </div>
  );
}
